/**
 * Admin Event Edit Page: smart form with sticky header and section navigation.
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconPlus,
  IconAlertTriangle,
  IconCopy,
  IconTrash,
  IconUsers,
  IconTicket,
  IconGift,
  IconHeartHands,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic, safeShowAlert } from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import { getCurrencySymbol } from "../../../lib/format.ts";
import { useEventEditor } from "./hooks/useEventEditor.ts";
import {
  useAdminEventPendingRsvps,
  useConfirmEventRsvp,
} from "./hooks";
import { EventEditorHeader } from "./components/EventEditorHeader.tsx";
import {
  EventEditorNav,
  type SectionNavItem,
} from "./components/EventEditorNav.tsx";
import { InlineDateTimeField } from "./components/InlineFields.tsx";
import { FullscreenTextEditor } from "./components/FullscreenTextEditor.tsx";
import { TicketTypeForm } from "./components/TicketTypeForm.tsx";
import {
  CoreSection,
  DescriptionSection,
  LocationSection,
  MediaSection,
  ContentSection,
  StreamingSection,
  TicketsSection,
  AccessSection,
  OrganizersSection,
} from "./components/sections/index.ts";
import { CollapsibleSection } from "./components/CollapsibleSection.tsx";
import type { TicketType } from "./types.ts";

const SECTION_IDS = {
  description: "section-description",
  location: "section-location",
  media: "section-media",
  content: "section-content",
  streaming: "section-streaming",
  tickets: "section-tickets",
  access: "section-access",
  organizers: "section-organizers",
} as const;

function DescriptionIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16M4 12h16M4 18h7"
      />
    </svg>
  );
}

function LocationIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
      />
    </svg>
  );
}

function MediaIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
      />
    </svg>
  );
}

function ContentIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
      />
    </svg>
  );
}

function StreamingIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"
      />
    </svg>
  );
}

function AccessIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
      />
    </svg>
  );
}

export function AdminEventEditPage() {
  const navigate = useNavigate();
  useParams<{ eventId: string }>();
  const tg = getTelegram();

  const editor = useEventEditor();
  const {
    event,
    eventId: id,
    isLoading,
    error,
    isSaving,
    isDeleting,
    isDuplicating,
    loadEvent,
    handleSave,
    handleDelete,
    handleDuplicate,
    handleImageUpload,
    handleBannerImageUpload,
    handleVideoUpload,
    formatDisplayDate,
    progressFilled,
    progressTotal,
    hasUnsavedChanges,
    imageCacheBust,
    bannerCacheBust,
    imageInputRef,
    bannerInputRef,
    videoInputRef,
    title,
    setTitle,
    subtitle,
    description,
    venue,
    setVenue,
    address,
    mapsUrl,
    setMapsUrl,
    startsAt,
    setStartsAt,
    endsAt,
    setEndsAt,
    maxAttendees,
    setMaxAttendees,
    isRecurring,
    setIsRecurring,
    rrule,
    setRrule,
    seriesConfig,
    setSeriesConfig,
    imageUrl,
    setImageUrl,
    bannerImageUrl,
    setBannerImageUrl,
    videoUrl,
    setVideoUrl,
    streamYoutubeUrl,
    setStreamYoutubeUrl,
    streamVkUrl,
    setStreamVkUrl,
    telegramChatUrl,
    setTelegramChatUrl,
    inviteText,
    slogan,
    program,
    status,
    setStatus,
    publishAt,
    setPublishAt,
    visibility,
    setVisibility,
    featured,
    setFeatured,
    genderRestriction,
    setGenderRestriction,
    minAge,
    setMinAge,
    maxAge,
    setMaxAge,
    selectedCategoryIds,
    toggleCategory,
    allCategories,
    homeGroupId,
    setHomeGroupId,
    requiresRsvpConfirmation,
    setRequiresRsvpConfirmation,
    homeGroups,
    showHomeGroupPicker,
    setShowHomeGroupPicker,
    isUploadingImage,
    isUploadingBannerImage,
    isUploadingVideo,
    videoUploadProgress,
    editingTicketType,
    setEditingTicketType,
    serviceFeeConfigId,
    setServiceFeeConfigId,
    serviceFeePrices,
    setServiceFeePrices,
    showStatusPicker,
    setShowStatusPicker,
    showVisibilityPicker,
    setShowVisibilityPicker,
    showGenderPicker,
    setShowGenderPicker,
    editingField,
    setEditingField,
    EVENT_STATUS_CONFIG,
    EVENT_VISIBILITY_CONFIG,
    GENDER_CONFIG,
    getEditorConfig,
  } = editor;

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [confirmingAccountId, setConfirmingAccountId] = useState<string | null>(
    null
  );

  // URQL hooks for pending RSVPs
  const {
    pendingRsvps,
    isLoading: isLoadingPendingRsvps,
    refetch: loadPendingRsvps,
  } = useAdminEventPendingRsvps({
    eventId: id,
    pause: !requiresRsvpConfirmation,
  });

  const { confirmRsvp } = useConfirmEventRsvp();

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/events");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  if (isLoading) {
    return <PageLoader message="Загрузка..." />;
  }

  if (error || !event) {
    return (
      <Layout>
        <div className="card p-6 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900 mb-3">
            {error || "Мероприятие не найдено"}
          </h2>
          <button
            onClick={() => navigate("/admin/events")}
            className="btn-primary"
          >
            К списку мероприятий
          </button>
        </div>
      </Layout>
    );
  }

  const isPublished = status === "PUBLISHED";
  const statusConfig = EVENT_STATUS_CONFIG[status];
  const statusOptions = Object.entries(EVENT_STATUS_CONFIG).map(
    ([value, cfg]) => ({
      value: value as keyof typeof EVENT_STATUS_CONFIG,
      label: cfg.label,
      badge: (
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{
            backgroundColor:
              value === "PUBLISHED"
                ? "#22c55e"
                : value === "SCHEDULED"
                ? "#3b82f6"
                : value === "CANCELLED"
                ? "#ef4444"
                : "#94a3b8",
          }}
        />
      ),
    })
  );

  const descFilled = [subtitle, description].filter(Boolean).length;
  const locationFilled =
    [venue, address, mapsUrl].filter(Boolean).length +
    (maxAttendees != null ? 1 : 0);
  const mediaFilled =
    (imageUrl ? 1 : 0) + (bannerImageUrl ? 1 : 0) + (videoUrl ? 1 : 0);
  const contentFilled = [inviteText, slogan, program].filter(Boolean).length;
  const streamingFilled = [
    streamYoutubeUrl,
    streamVkUrl,
    telegramChatUrl,
  ].filter(Boolean).length;
  const ticketsFilled = event.ticketTypes.length > 0 ? 1 : 0;
  const accessFilled =
    (selectedCategoryIds.length > 0 ? 1 : 0) +
    (genderRestriction !== "ANY" ? 1 : 0) +
    (minAge != null || maxAge != null ? 1 : 0) +
    (visibility !== "PUBLIC" ? 1 : 0);
  const organizersFilled = event.organizers.length > 0 ? 1 : 0;

  const navSections: SectionNavItem[] = [
    { id: SECTION_IDS.description, label: "Описание", filled: descFilled >= 2 },
    { id: SECTION_IDS.location, label: "Место", filled: locationFilled >= 2 },
    { id: SECTION_IDS.media, label: "Медиа", filled: mediaFilled >= 1 },
    { id: SECTION_IDS.content, label: "Контент", filled: contentFilled >= 1 },
    {
      id: SECTION_IDS.streaming,
      label: "Трансляции",
      filled: streamingFilled >= 1,
    },
    { id: SECTION_IDS.tickets, label: "Билеты", filled: ticketsFilled >= 1 },
    { id: SECTION_IDS.access, label: "Доступ", filled: accessFilled >= 1 },
    {
      id: SECTION_IDS.organizers,
      label: "Организаторы",
      filled: organizersFilled >= 1,
    },
  ];

  const editorConfig = getEditorConfig();

  const openTicketType = (tt: TicketType) => {
    haptic("light");
    setEditingTicketType(tt);
    const serviceFeeLink = tt.fees?.find(
      (f) => f.feeConfiguration?.type === "SERVICE"
    );
    if (serviceFeeLink?.feeConfiguration) {
      const fc = serviceFeeLink.feeConfiguration;
      setServiceFeeConfigId(fc.id);
      const prices: Record<string, string> = { THB: "", RUB: "" };
      for (const p of fc.prices || []) {
        prices[p.currency] = String(p.amount ?? "");
      }
      setServiceFeePrices(prices);
    }
  };

  return (
    <Layout>
      <div className="pb-6">
        <EventEditorHeader
          title={title}
          statusLabel={statusConfig.label}
          statusBgColor={statusConfig.bgColor}
          statusColor={statusConfig.color}
          imageUrl={imageUrl}
          imageCacheBust={imageCacheBust}
          startsAtDisplay={startsAt ? formatDisplayDate(startsAt) : ""}
          progressFilled={progressFilled}
          progressTotal={progressTotal}
          isSaving={isSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleSave}
          onPreview={() => navigate(`/event/${id}?preview=true`)}
          className="mb-4"
        />

        {isPublished && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-start gap-2">
            <IconAlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-medium text-amber-800">
                Редактирование опубликованного мероприятия
              </p>
              <p className="text-[12px] text-amber-700 mt-0.5">
                Изменения сразу станут видны пользователям
              </p>
            </div>
          </div>
        )}

        <EventEditorNav
          sections={navSections}
          activeSectionId={activeSectionId}
          onSectionClick={setActiveSectionId}
          className="mb-4"
        />

        <CoreSection
          title={title}
          setTitle={setTitle}
          startsAt={startsAt}
          setStartsAt={setStartsAt}
          endsAt={endsAt}
          setEndsAt={setEndsAt}
          status={status}
          setStatus={setStatus}
          statusConfig={statusConfig}
          statusOptions={statusOptions}
          showStatusPicker={showStatusPicker}
          setShowStatusPicker={setShowStatusPicker}
          InlineDateTimeFieldPublish={
            status === "SCHEDULED" ? (
              <InlineDateTimeField
                label="Публикация"
                value={publishAt}
                onChange={setPublishAt}
              />
            ) : undefined
          }
        />

        <div className="space-y-4 mb-6">
          <DescriptionSection
            id={SECTION_IDS.description}
            subtitle={subtitle}
            description={description}
            setEditingField={setEditingField}
            icon={<DescriptionIcon />}
            summary={
              descFilled === 0
                ? "Не заполнено"
                : descFilled >= 2
                ? "Подзаголовок и описание заданы"
                : "Частично заполнено"
            }
            filledCount={descFilled}
            totalCount={2}
            hint="Краткое описание показывается в карточке события."
          />

          <LocationSection
            id={SECTION_IDS.location}
            venue={venue}
            setVenue={setVenue}
            address={address}
            setEditingField={setEditingField}
            mapsUrl={mapsUrl}
            setMapsUrl={setMapsUrl}
            maxAttendees={maxAttendees}
            setMaxAttendees={setMaxAttendees}
            isRecurring={isRecurring}
            setIsRecurring={setIsRecurring}
            rrule={rrule}
            setRrule={setRrule}
            seriesConfig={seriesConfig}
            setSeriesConfig={setSeriesConfig}
            homeGroupId={homeGroupId}
            setHomeGroupId={setHomeGroupId}
            homeGroups={homeGroups}
            requiresRsvpConfirmation={requiresRsvpConfirmation}
            setRequiresRsvpConfirmation={setRequiresRsvpConfirmation}
            showHomeGroupPicker={showHomeGroupPicker}
            setShowHomeGroupPicker={setShowHomeGroupPicker}
            icon={<LocationIcon />}
            summary={venue ? venue : "Место не задано"}
            filledCount={Math.min(locationFilled, 4)}
            totalCount={4}
            hint="Укажите место проведения и адрес."
          />

          {requiresRsvpConfirmation &&
            (isLoadingPendingRsvps || pendingRsvps.length > 0) && (
              <CollapsibleSection
                title="Ожидают подтверждения"
                defaultOpen={pendingRsvps.length > 0}
                icon={<IconUsers className="w-5 h-5" />}
                badge={
                  pendingRsvps.length > 0 ? (
                    <span className="text-[11px] text-amber-600 font-medium">
                      {pendingRsvps.length}
                    </span>
                  ) : undefined
                }
              >
                {isLoadingPendingRsvps ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    Загрузка...
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {pendingRsvps.map((r) => {
                      const name =
                        [r.account.firstName, r.account.lastName]
                          .filter(Boolean)
                          .join(" ") || "Участник";
                      return (
                        <li
                          key={r.id}
                          className="flex items-center justify-between gap-2 min-h-[52px] px-4 py-3"
                        >
                          <span className="text-[15px] text-slate-900 min-w-0 flex-1">
                            {name}
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!id || !r.accountId) return;
                              setConfirmingAccountId(r.accountId);
                              try {
                                await confirmRsvp(id, r.accountId);
                                haptic("success");
                                loadPendingRsvps();
                              } catch (err) {
                                haptic("error");
                                safeShowAlert(getErrorMessage(err, "Ошибка"));
                              } finally {
                                setConfirmingAccountId(null);
                              }
                            }}
                            disabled={confirmingAccountId === r.accountId}
                            className="px-3 py-1.5 text-[13px] font-medium text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200/60 active:scale-[0.98] disabled:opacity-50"
                          >
                            {confirmingAccountId === r.accountId
                              ? "…"
                              : "Подтвердить"}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CollapsibleSection>
            )}

          <MediaSection
            id={SECTION_IDS.media}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            bannerImageUrl={bannerImageUrl}
            setBannerImageUrl={setBannerImageUrl}
            videoUrl={videoUrl}
            setVideoUrl={setVideoUrl}
            imageCacheBust={imageCacheBust}
            bannerCacheBust={bannerCacheBust}
            isUploadingImage={isUploadingImage}
            isUploadingBannerImage={isUploadingBannerImage}
            isUploadingVideo={isUploadingVideo}
            videoUploadProgress={videoUploadProgress}
            imageInputRef={imageInputRef}
            bannerInputRef={bannerInputRef}
            videoInputRef={videoInputRef}
            onImageUpload={handleImageUpload}
            onBannerImageUpload={handleBannerImageUpload}
            onVideoUpload={handleVideoUpload}
            icon={<MediaIcon />}
            summary={
              imageUrl && videoUrl
                ? "Обложка и видео загружены"
                : imageUrl
                ? "Обложка загружена"
                : videoUrl
                ? "Видео загружено"
                : "Медиа не добавлены"
            }
            filledCount={mediaFilled}
            totalCount={3}
            hint="Обложка — в карточке события. Баннер — горизонтальное изображение в карусели на главной."
          />

          <ContentSection
            id={SECTION_IDS.content}
            inviteText={inviteText}
            slogan={slogan}
            program={program}
            setEditingField={setEditingField}
            icon={<ContentIcon />}
            summary={
              contentFilled === 0
                ? "Не заполнено"
                : `${contentFilled} из 3 полей`
            }
            filledCount={contentFilled}
            totalCount={3}
            hint="Программа и слоган отображаются на странице события."
          />

          <StreamingSection
            id={SECTION_IDS.streaming}
            streamYoutubeUrl={streamYoutubeUrl}
            setStreamYoutubeUrl={setStreamYoutubeUrl}
            streamVkUrl={streamVkUrl}
            setStreamVkUrl={setStreamVkUrl}
            telegramChatUrl={telegramChatUrl}
            setTelegramChatUrl={setTelegramChatUrl}
            icon={<StreamingIcon />}
            summary={
              streamingFilled === 0
                ? "Ссылки не добавлены"
                : `${streamingFilled} ссылок`
            }
            filledCount={streamingFilled}
            totalCount={3}
          />

          <TicketsSection
            id={SECTION_IDS.tickets}
            icon={<IconTicket className="w-5 h-5" />}
            summary={
              event.ticketTypes.length === 0
                ? "Нет типов билетов"
                : `${event.ticketTypes.length} тип(ов)`
            }
            filledCount={ticketsFilled}
            totalCount={1}
            hint="Добавьте типы билетов, если мероприятие платное."
          >
            <button
              type="button"
              onClick={() => setEditingTicketType("new")}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-200 rounded-xl text-[var(--color-accent)] font-medium active:border-[var(--color-accent)] transition-colors"
            >
              <IconPlus className="w-5 h-5" />
              Добавить тип билета
            </button>
            {editingTicketType !== null && id && (
              <TicketTypeForm
                ticketType={editingTicketType}
                eventId={id}
                onSave={() => {
                  setEditingTicketType(null);
                  loadEvent();
                }}
                onDelete={
                  editingTicketType !== "new"
                    ? () => {
                        setEditingTicketType(null);
                        loadEvent();
                      }
                    : undefined
                }
                onCancel={() => setEditingTicketType(null)}
                serviceFeeConfigId={serviceFeeConfigId}
                serviceFeePrices={serviceFeePrices}
                setServiceFeePrices={setServiceFeePrices}
              />
            )}
            {event.ticketTypes.map((tt) => {
              const pricesDisplay = (tt.prices || [])
                .map(
                  (p) =>
                    `${parseFloat(
                      p.price
                    ).toLocaleString()} ${getCurrencySymbol(p.currency)}`
                )
                .join(" / ");
              return (
                <button
                  key={tt.id}
                  type="button"
                  onClick={() => openTicketType(tt)}
                  className="w-full p-3 bg-slate-50 active:bg-slate-100 rounded-xl text-left transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 text-[15px]">
                        {tt.name}
                      </span>
                      {!tt.isActive && (
                        <span className="px-2 py-0.5 text-[10px] bg-slate-200 text-slate-600 rounded-full">
                          Неактивен
                        </span>
                      )}
                    </div>
                    <span className="font-semibold text-slate-900 text-[15px]">
                      {pricesDisplay ||
                        `${parseFloat(
                          tt.price
                        ).toLocaleString()} ${getCurrencySymbol(tt.currency)}`}
                    </span>
                  </div>
                  <div className="flex gap-3 mt-1 text-[12px] text-slate-500">
                    {tt.maxQuantity != null && (
                      <span>Лимит: {tt.maxQuantity}</span>
                    )}
                    <span>Продано: {tt.soldCount}</span>
                  </div>
                </button>
              );
            })}
          </TicketsSection>

          {/* Raffle button */}
          {event.ticketTypes.length > 0 && (
            <button
              type="button"
              onClick={() => navigate(`/admin/raffle/event/${id}`)}
              className="w-full card p-4 flex items-center gap-3 active:bg-slate-50 transition-colors"
            >
              <div className="p-2 rounded-xl bg-purple-100 text-purple-600">
                <IconGift className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-slate-900">Розыгрыши</h3>
                <p className="text-sm text-slate-500">
                  Создайте розыгрыш призов для владельцев билетов
                </p>
              </div>
              <svg
                className="w-5 h-5 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}

          {/* Gender Party button */}
          <button
            type="button"
            onClick={() => navigate(`/admin/gender-party?eventId=${id}`)}
            className="w-full card p-4 flex items-center gap-3 active:bg-slate-50 transition-colors"
          >
            <div className="p-2 rounded-xl bg-pink-100 text-pink-600">
              <IconHeartHands className="w-6 h-6" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-medium text-slate-900">Gender Party</h3>
              <p className="text-sm text-slate-500">
                Голосование для определения пола ребёнка
              </p>
            </div>
            <svg
              className="w-5 h-5 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <AccessSection
            id={SECTION_IDS.access}
            visibility={visibility}
            setVisibility={setVisibility}
            visibilityOptions={Object.entries(EVENT_VISIBILITY_CONFIG).map(
              ([value, cfg]) => ({
                value: value as keyof typeof EVENT_VISIBILITY_CONFIG,
                label: cfg.label,
                description: cfg.description,
              })
            )}
            showVisibilityPicker={showVisibilityPicker}
            setShowVisibilityPicker={setShowVisibilityPicker}
            genderRestriction={genderRestriction}
            setGenderRestriction={setGenderRestriction}
            genderOptions={Object.entries(GENDER_CONFIG).map(
              ([value, cfg]) => ({
                value: value as keyof typeof GENDER_CONFIG,
                label: cfg.label,
              })
            )}
            showGenderPicker={showGenderPicker}
            setShowGenderPicker={setShowGenderPicker}
            minAge={minAge}
            setMinAge={setMinAge}
            maxAge={maxAge}
            setMaxAge={setMaxAge}
            featured={featured}
            setFeatured={setFeatured}
            selectedCategoryIds={selectedCategoryIds}
            toggleCategory={toggleCategory}
            allCategories={allCategories}
            icon={<AccessIcon />}
            summary={
              selectedCategoryIds.length > 0
                ? `Категории: ${selectedCategoryIds.length}`
                : "Без ограничений"
            }
            filledCount={Math.min(accessFilled, 4)}
            totalCount={4}
          />

          {id && (
            <OrganizersSection
              id={SECTION_IDS.organizers}
              eventId={id}
              organizers={event.organizers}
              onUpdate={loadEvent}
              icon={<IconUsers className="w-5 h-5" />}
              summary={`${event.organizers.length} организаторов`}
              filledCount={organizersFilled}
              totalCount={1}
            />
          )}
        </div>

        <div className="card overflow-hidden mb-5">
          <button
            type="button"
            onClick={() => handleDuplicate(navigate)}
            disabled={isDuplicating || isSaving || isDeleting}
            className="w-full px-4 py-3.5 flex items-center justify-center gap-2 text-slate-600 font-medium text-[15px] active:bg-slate-50 transition-colors disabled:opacity-50 border-b border-slate-100"
          >
            {isDuplicating ? (
              <span className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
            ) : (
              <IconCopy className="w-5 h-5" />
            )}
            {isDuplicating ? "Копирование..." : "Дублировать мероприятие"}
          </button>
          <button
            type="button"
            onClick={() => handleDelete(navigate)}
            disabled={isSaving || isDeleting || isDuplicating}
            className="w-full px-4 py-3.5 flex items-center justify-center gap-2 text-red-500 font-medium text-[15px] active:bg-red-50 transition-colors disabled:opacity-50"
          >
            {isDeleting ? (
              <span className="w-5 h-5 border-2 border-red-200 border-t-red-500 rounded-full animate-spin" />
            ) : (
              <IconTrash className="w-5 h-5" />
            )}
            {isDeleting ? "Удаление..." : "Удалить мероприятие"}
          </button>
        </div>

        <div
          className="h-4"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        />

        {editorConfig && (
          <FullscreenTextEditor
            isOpen={editingField !== null}
            title={editorConfig.title}
            value={editorConfig.value}
            placeholder={editorConfig.placeholder}
            onSave={editorConfig.onSave}
            onClose={() => setEditingField(null)}
          />
        )}
      </div>
    </Layout>
  );
}
